import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  startTransition,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import FragmentCard from "./FragmentCard";
import { useToast } from "../common/ToastContext";
import AccessDenied from "../common/AccessDenied";
import { currentUserHasRole } from "../../services/userService";
import promptFragmentsService from "../../services/promptFragmentsService";
import ImportModal from "../common/ImportModal";
import usePins from "../../hooks/usePins";

const FragmentsDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const [fragments, setFragments] = useState([]);
  const [selectedFragment, setSelectedFragment] = useState(null);
  const [fragmentVersions, setFragmentVersions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const [importing, setImporting] = useState(false);
  const [canImport, setCanImport] = useState(false);
  const { sortPinnedFirst } = usePins("fragments");
  const orderedFragments = useMemo(
    () => sortPinnedFirst(fragments, (f) => f.id),
    [fragments, sortPinnedFirst],
  );

  const FragmentsEditor = React.lazy(() => import("./FragmentsEditor"));

  const fetchFragments = useCallback(async () => {
    try {
      const fragmentsData = (await promptFragmentsService.getFragments()) || [];
      const sortedFragments = [...fragmentsData].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFragments(sortedFragments);
    } catch (error) {
      console.error("Error fetching fragments:", error);
      showToast("Failed to fetch fragments", "error");
    }
  }, [showToast]);

  const fetchSelectedFragment = useCallback(
    async (fragment) => {
      if (fragment && fragment.id) {
        try {
          const versionsResponse =
            await promptFragmentsService.getFragmentVersionsById(fragment.id);
          const versions = versionsResponse || [];

          const sortedVersions = [...versions].sort((a, b) => b.id - a.id);
          setFragmentVersions(sortedVersions);

          const versionDetails = sortedVersions[0];

          setSelectedFragment({
            ...fragment,
            content: versionDetails.content || "",
            version: sortedVersions.length.toString(),
            versionId: versionDetails.id,
            latest_version: sortedVersions.length,
          });
        } catch (error) {
          console.error("Error fetching selected fragment:", error);
          showToast("Failed to fetch selected fragment", "error");
        }
      }
    },
    [selectedFragment, showToast]
  );

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasReadAccess = await currentUserHasRole("viewer");
        setAccessDenied(!hasReadAccess);
        if (hasReadAccess) {
          fetchFragments();
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        showToast("Failed checking user role.", "error");
        setAccessDenied(true);
      }
    };
    checkAccess();
  }, [fetchFragments, showToast]);

  useEffect(() => {
    const checkImportAccess = async () => {
      try {
        const hasEngineerAccess = await currentUserHasRole("prompt-engineer");
        setCanImport(hasEngineerAccess);
      } catch (error) {
        setCanImport(false);
      }
    };
    checkImportAccess();
  }, []);

  useEffect(() => {
    if (searchParams.has("id") && !selectedFragment) {
      const editFragmentId = searchParams.get("id");
      const editFragment = fragments.find(
        (fragment) => fragment.id === editFragmentId
      );
      if (editFragment) {
        fetchSelectedFragment(editFragment);
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [
    searchParams,
    fragments,
    selectedFragment,
    setSearchParams,
    fetchSelectedFragment,
  ]);

  const handleSaveFragment = async (fragmentData) => {
    let isUpdated,
      isCreated = 0;

    try {
      if (fragmentData.name === "") {
        showToast("Please enter a name.", "error");
        return;
      }

      if (fragmentData.id) {
        const updates = [];

        if (hasFragmentMetadataChanged(fragmentData, selectedFragment)) {
          isUpdated = 1;
          updates.push(
            promptFragmentsService.updateFragment({
              id: fragmentData.id,
              defaultFlag: false,
              deleted: false,
              description: fragmentData.description,
              fragmentName: fragmentData.name,
            })
          );
        }

        if (fragmentData.content !== selectedFragment.content) {
          isCreated = 1;

          updates.push(
            promptFragmentsService.createFragmentVersion({
              fragment_id: fragmentData.id,
              content: fragmentData.content,
            })
          );
        }

        if (updates.length > 0) {
          await Promise.all(updates);
        }
      } else {
        isCreated = 1;
        const createdFragment = await promptFragmentsService.createFragment({
          block_name: fragmentData.name,
          description: fragmentData.description,
          name: fragmentData.name,
        });

        if (!createdFragment || !createdFragment.id) {
          throw new Error("Failed to create fragment");
        }

        const versionResponse =
          await promptFragmentsService.createFragmentVersion({
            fragment_id: createdFragment.id,
            content: fragmentData.content || "",
          });

        fragmentData.id = createdFragment.id;
        fragmentData.block_name = createdFragment.block_name;

        const newFragment = {
          ...createdFragment,
          content: fragmentData.content || "",
          versions: [versionResponse],
        };

        setFragments((prevFragments) => {
          const updatedFragments = [...prevFragments, newFragment];
          return updatedFragments.sort((a, b) => a.name.localeCompare(b.name));
        });
      }

      if (isUpdated) {
        const newFrag = await promptFragmentsService.getFragmentById(
          fragmentData.id
        );
        setFragments((prevFragments) =>
          prevFragments.map((item) =>
            item.id === fragmentData.id ? newFrag : item
          )
        );
        handleCloseFragmentEditor();
        handleFragmentClick(newFrag);
      } else if (isCreated && !isUpdated) {
        handleCloseFragmentEditor();
        handleFragmentClick(fragmentData);
      }

      showToast("Fragment saved successfully", "info");
    } catch (error) {
      console.error("Error saving fragment:", error);
      showToast(`Error saving fragment: ${error}`, "error");
    }
  };

  function hasFragmentMetadataChanged(newData, oldData) {
    return (
      newData.default !== oldData.default ||
      newData.deleted !== oldData.deleted ||
      newData.description !== oldData.description ||
      newData.name !== oldData.name
    );
  }

  const handleDeleteFragment = async (fragmentId) => {
    try {
      await promptFragmentsService.deleteFragment(fragmentId);
      await fetchFragments();
      handleCloseFragmentEditor();
      showToast("Fragment deleted successfully", "info");
    } catch (error) {
      console.error("Error deleting fragment:", error);
      showToast(`Error deleting fragment: ${error}`, "error");
    }
  };

  const handleCloseFragmentEditor = () => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the editor.
    startTransition(() => setSelectedFragment(null));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  };

  const handleAddNewFragment = async () => {
    try {
      const newFragment = {
        name: "",
        description: "",
        content: "",
        isNew: true,
      };

      setSelectedFragment(newFragment);
      setFragmentVersions([]);
    } catch (error) {
      console.error("Error creating new fragment:", error);
      showToast(`Error creating new fragment: ${error}`, "error");
    }
  };

  const handleFragmentClick = async (fragment) => {
    try {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("id", fragment.id);
        return newParams;
      });
    } catch (error) {
      console.error("Error fetching fragment versions:", error);
      showToast("Failed to fetch fragment versions", "error");
    }
  };

  const handleFragmentImport = async (fragmentData, forced) => {
    try {
      const importedFragments = await Promise.all(
        fragmentData.map((frag) => {
          const importedFragment = promptFragmentsService.importFragment({
            ...frag,
            forced,
          });
          return importedFragment;
        })
      );
      if (!importedFragments || importedFragments.length === 0) {
        showToast("No fragment data to import", "error");
        return;
      }

      showToast("Fragment imported successfully", "success");
      await fetchFragments();
      setImporting(false);
    } catch (error) {
      console.error("Error importing fragment:", error);
      showToast("Failed to import fragment", "error");
    }
  };

  const handleClickImport = () => {
    setImporting(true);
  };

  if (accessDenied === null) {
    return <div>Loading...</div>;
  }

  if (accessDenied) {
    return <AccessDenied />;
  }

  const getAddedContextMenuItems = () => {
    let items;
    if (canImport) {
      items = [{ label: "Import", onClick: handleClickImport }];
    } else {
      items = [];
    }
    return items;
  };

  return (
    <DesktopView
      items={orderedFragments}
      selectedItem={selectedFragment || importing}
      searchPlaceholder="Filter fragments ..."
      contextMenuItems={getAddedContextMenuItems()}
      onItemClick={handleFragmentClick}
      onSave={handleSaveFragment}
      onDelete={handleDeleteFragment}
      onReset={() => {}}
      onClose={handleCloseFragmentEditor}
      Card={FragmentCard}
      Editor={() => (
        <React.Suspense fallback={<div>Loading editor...</div>}>
          {selectedFragment && (
            <FragmentsEditor
              item={selectedFragment}
              versions={fragmentVersions || []}
              onSave={handleSaveFragment}
              onClose={handleCloseFragmentEditor}
              onDelete={handleDeleteFragment}
              isCreating={!selectedFragment.id}
            />
          )}
          {importing && (
            <ImportModal
              onClose={() => setImporting(false)}
              onImport={handleFragmentImport}
              importType={"fragment"}
            />
          )}
        </React.Suspense>
      )}
      addNewItem={handleAddNewFragment}
      eventName="fragmentCreated"
    />
  );
};

export default FragmentsDesktop;
